import React, { useState, useEffect } from 'react';
import { Search, Download, FileText, Clock } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000';

function Dashboard() {
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    fetchTasks();
    const interval = setInterval(fetchTasks, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_BASE}/tasks`);
      console.log('Tasks data:', response.data); // Debug log
      // Log each task's status and progress
      response.data.forEach(task => {
        console.log(`Task ${task.filename}: status=${task.status}, progress=${task.progress}`);
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTasks, setFilteredTasks] = useState(tasks);

  useEffect(() => {
    const filtered = tasks.filter(task => 
      task.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.airline.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTasks(filtered);
  }, [tasks, searchTerm]);

  const handleDownload = async (taskId, outputFilename) => {
    try {
      const response = await axios.get(`${API_BASE}/download/${taskId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', outputFilename || 'output.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed');
    }
  };

  const handleDownloadFailed = async (taskId, failedFilename) => {
    try {
      const response = await axios.get(`${API_BASE}/download-failed/${taskId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', failedFilename || 'failed_pnrs.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed download error:', error);
      alert('Failed PNRs file not available');
    }
  };

  const handleDownloadInput = async (taskId, filename) => {
    try {
      const response = await axios.get(`${API_BASE}/download-input/${taskId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Input download error:', error);
      alert('Input file not available');
    }
  };

  const handleStop = async (taskId) => {
    if (!window.confirm('Are you sure you want to stop processing? Partial results will be saved.')) {
      return;
    }
    
    try {
      await axios.post(`${API_BASE}/stop/${taskId}`);
      alert('Stop request sent. Task will stop after current batch completes.');
      fetchTasks();
    } catch (error) {
      console.error('Stop failed:', error);
      alert('Failed to stop task');
    }
  };

  const handleResume = async (taskId) => {
    if (!window.confirm('Resume processing remaining PNRs for this task?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE}/resume/${taskId}`);
      alert(response.data.message || 'Task resumed.');
      fetchTasks();
    } catch (error) {
      console.error('Resume failed:', error);
      const msg = error.response?.data?.error || 'Failed to resume task';
      alert(msg);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', text: 'Completed' },
      processing: { color: 'bg-blue-100 text-blue-800', text: 'Processing' },
      failed: { color: 'bg-red-100 text-red-800', text: 'Failed' },
      stopped: { color: 'bg-orange-100 text-orange-800', text: 'Stopped' },
      queued: { color: 'bg-yellow-100 text-yellow-800', text: 'Queued' }
    };
    const config = statusConfig[status] || statusConfig.queued;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getProgressBar = (progress, status) => {
    const progressValue = progress || 0;
    const color = status === 'failed' ? 'bg-red-500' : 
                  status === 'completed' ? 'bg-green-500' : 
                  status === 'stopped' ? 'bg-orange-500' : 
                  'bg-blue-500';
    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${progressValue}%` }}></div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Flight Data Processing Dashboard</h1>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by filename or airline..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full max-w-md border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No.</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date of Upload</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Airline Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Input File</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Final Output File</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTasks.map((task, index) => (
              <tr key={task.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(task.uploadedAt).toLocaleDateString()}
                  <br />
                  <span className="text-xs text-gray-500">
                    {new Date(task.uploadedAt).toLocaleTimeString()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900">{task.airline}</span>
                    <div className="ml-2">{getStatusBadge(task.status)}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">{getProgressBar(task.progress, task.status)}</div>
                    <span className="text-sm text-gray-600">{task.progress || 0}%</span>
                  </div>
                  {(task.status === 'completed' || task.status === 'stopped') && (task.successfulCount > 0 || task.failedCount > 0) && (
                    <div className="text-xs text-gray-500 mt-1">
                      ✅ {task.successfulCount} success, ❌ {task.failedCount} failed
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{task.filename}</span>
                    {task.hasInputFile && (
                      <button
                        onClick={() => handleDownloadInput(task.id, task.inputFilename)}
                        className="ml-2 inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                        title="Download input file"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Input
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {task.status === 'completed' || task.status === 'stopped' ? (
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => handleDownload(task.id, task.outputFilename)}
                        className="inline-flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        <span className="text-sm">{task.outputFilename || 'Download Results'}</span>
                      </button>
                      <button
                        onClick={() => handleDownloadFailed(task.id, task.failedFilename)}
                        disabled={!task.hasFailedFile}
                        className={`inline-flex items-center space-x-1 px-3 py-1 rounded-md transition-colors ${
                          task.hasFailedFile 
                            ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                        title={task.hasFailedFile ? 'Download failed PNRs' : 'No failed PNRs'}
                      >
                        <Download className="w-4 h-4" />
                        <span className="text-sm">{task.failedFilename || 'Failed PNRs'}</span>
                      </button>
                      {task.status === 'stopped' && (
                        <button
                          onClick={() => handleResume(task.id)}
                          className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                        >
                          <span className="text-sm">▶ Resume Processing</span>
                        </button>
                      )}
                    </div>
                  ) : task.status === 'processing' ? (
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center space-x-1 text-blue-600">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">Processing...</span>
                      </div>
                      <button
                        onClick={() => handleStop(task.id)}
                        className="inline-flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                      >
                        <span className="text-sm">⏹ Stop Processing</span>
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Not available</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchTerm ? 'No tasks match your search.' : 'No processing tasks yet. Use the Upload Data page to submit files for processing.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;